import { useEffect, useInsertionEffect, useLayoutEffect, useState } from 'react';
let styleElement = null;
const useIsomorphicInsertionEffect = typeof window !== 'undefined' && typeof document !== 'undefined'
    ? (typeof useInsertionEffect === 'function' ? useInsertionEffect : useLayoutEffect)
    : useEffect;
function ensureStyleElement() {
    if (styleElement && document.head.contains(styleElement))
        return styleElement;
    // Prefer an existing server-injected style bucket with correct nonce
    const existing = document.getElementById('nonce-style-bucket');
    if (existing) {
        styleElement = existing;
        return styleElement;
    }
    styleElement = document.createElement('style');
    styleElement.setAttribute('data-use-nonce-style', '');
    // Read the CSP nonce from meta (or adapt to your actual nonce source)
    const nonce = document.querySelector('meta[name="csp-nonce"]')?.getAttribute('content') ?? '';
    if (nonce) {
        styleElement.setAttribute('nonce', nonce);
    }
    document.head.appendChild(styleElement);
    return styleElement;
}
function styleToString(style) {
    return Object.entries(style)
        .map(([key, value]) => {
        const prop = key.replace(/([A-Z])/g, '-$1').toLowerCase();
        const val = typeof value === 'number' ? `${value}px` : String(value);
        return `${prop}:${val}`;
    })
        .join(';');
}
export default function useNonceStyle(style) {
    const [className] = useState(() => `ns-${Math.random().toString(36).slice(2)}`);
    useIsomorphicInsertionEffect(() => {
        const el = ensureStyleElement();
        const cssText = `.${className}{${styleToString(style)}}`;
        let index = -1;
        let rafId = 0;
        let cleaned = false;
        const tryInsert = () => {
            if (cleaned)
                return;
            const sheet = el.sheet;
            if (!sheet) {
                // Wait a frame for the sheet to become available
                rafId = requestAnimationFrame(tryInsert);
                return;
            }
            try {
                index = sheet.insertRule(cssText, sheet.cssRules.length);
            }
            catch {
                // Fallback: append as text if insertRule fails (still requires CSP to allow it)
                el.appendChild(document.createTextNode(cssText));
            }
        };
        tryInsert();
        return () => {
            cleaned = true;
            if (rafId)
                cancelAnimationFrame(rafId);
            const sheet = el.sheet;
            if (sheet && index !== -1) {
                try {
                    sheet.deleteRule(index);
                }
                catch (e) {
                    // ignore cleanup errors
                }
            }
        };
    }, [className, style]);
    return className;
}
